import React, { useRef } from "react";
import { TextField, InputAdornment, IconButton, Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { InsertEmoticon, Mic, Stop, Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";

const MessageWordpressChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  isRecording,
  recordingDuration = 0,
  startRecording,
  stopRecording,
  discardRecording = () => {},
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onRemoveAudio,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputBorderRadius = 16;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
  });
  const canUseAiSuggestion = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const canSend = composerHasSendPayload(message, file, files, audioURL);

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder={channelType === "Xpro" ? "Registrar uma anotação" : `Envie uma mensagem via ${channelType}`}
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      inputProps={composeInputHandlers}
      InputProps={{
        style: { borderRadius: 0, fontSize: isMobile ? "0.95rem" : "1rem" },
        inputRef: inputRef,
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              onClick={handleEmojiPickerOpen}
              edge="start"
              size={isMobile ? "small" : "medium"}
              aria-label="Emoji"
            >
              <InsertEmoticon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}>
            {canUseAiSuggestion ? (
              <Tooltip
                title={
                  aiDisabled
                    ? isGeneratingSuggestion
                      ? "Gerando resposta..."
                      : "Disponível após a conversa ter histórico."
                    : "Sugerir resposta com IA"
                }
              >
                <span>
                  <IconButton onClick={onGenerateSuggestion} disabled={aiDisabled} size={isMobile ? "small" : "medium"}>
                    <AutoAwesome fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu openFilePicker={openFilePicker} showAttachProducts={false} showLocation={false} size={isMobile ? "small" : "medium"} />
            {/* Mic/send principal fica no botão externo do composer (MessageInput) */}
          </InputAdornment>
        ),
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        "& .MuiInputBase-root": {
          minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
          boxSizing: "border-box",
          borderRadius: "0 !important",
          overflow: "hidden",
          clipPath: "none",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "0 !important",
          overflow: "hidden",
          clipPath: "none",
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
        },
        "& .MuiOutlinedInput-notchedOutline": { border: "none !important", borderRadius: "0 !important" },
        "& textarea": { overflowY: "auto", resize: "none", boxSizing: "border-box", borderRadius: "inherit", overflowX: "hidden", padding: "4px 10px" },
      }}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
  ) : (
    textField
  );

  const integratedComposer = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "stretch",
        borderRadius: `${inputBorderRadius}px`,
        minHeight: COMPOSER_ROW_HEIGHT_PX,
        overflow: "hidden",
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "stretch",
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector integrated selectedChannel={selectedChannel} setSelectedChannel={setSelectedChannel} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {textOrVoiceSlot}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", paddingBottom: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: isMobile ? 0.5 : 0, width: "100%" }}>
        {integratedComposer}
        {isMobile ? (
          <IconButton
            onClick={() => {
              if (canSend) handleSendMessage();
              else startRecording();
            }}
            aria-label={canSend ? "Enviar" : "Gravar áudio"}
            sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "50%",
              p: 1,
              flexShrink: 0,
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            {canSend ? <Send fontSize="small" /> : <Mic fontSize="small" />}
          </IconButton>
        ) : null}
      </Box>
      {selectionToolbar}
    </Box>
  );
};

export default MessageWordpressChannel;
